import React from "react";
import moment from "moment";
import momentTimeZone from "moment-timezone";
import Countdown from "react-countdown";
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { MdSignalWifiStatusbarConnectedNoInternet as WifiSVG } from "react-icons/md";

import CountdownRenderer from "./CountdownRenderer";
import { getUserData } from "../../../../common";

const capitalize = (str) => {
  const capitalizedWords = str
    .toLowerCase()
    .split(" ")
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
  return capitalizedWords.join(" ");
};

const getLocationString = (event, location) => {
  if (location) return location;
  if (event?.city && event?.country) return `${event.city}, ${event.country}`;
  if (event?.city) return `${event?.city}`;
  if (event?.country) return `${event?.country}`;
  return "";
};

const createProxyBidListLots = (
  lots,
  styles,
  userAmount,
  eventDetails,
  handleProxyAmountChange,
  isForwardAuction,
  isOnline,
  lotBidEnded,
  setProxyBid
) => {
  const timeZone = momentTimeZone.tz.guess();
  const userData = getUserData();
  let currentTime = moment().tz(timeZone).format();

  const columns = [
    {
      name: "Lot No.",
      width: "90px",
      selector: (data) => capitalize(String(data.lotNumber)),
      sortable: true,
    },
    {
      name: "Description",
      width: "140px",
      selector: (data) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Tooltip
            title={String(data.shortDescription)}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: "none",
                  whiteSpace: "pre-wrap",
                  bgcolor: "background.paper",
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 1,
                  fontSize: "14px",
                },
              },
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
              }}
            >
              {String(data.shortDescription)}
            </span>
          </Tooltip>
          <Tooltip
            title={String(data.longDescription)}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: "none",
                  whiteSpace: "pre-wrap",
                  bgcolor: "background.paper",
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 1,
                  fontSize: "14px",
                },
              },
            }}
          >
            <Button
              variant="text"
              size="small"
              sx={{
                minWidth: "24px",
                width: "24px",
                height: "24px",
                p: 0,
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                  bgcolor: "action.hover",
                },
              }}
            >
              <InfoIcon
                sx={{
                  fontSize: "18px",
                  color: "#3D90D7",
                  position: "absolute",
                }}
              />
            </Button>
          </Tooltip>
        </Box>
      ),
      sortable: false,
    },
    {
      name: "Start Price",
      width: "120px",
      selector: (data) => String(Number(data.startPrice)),
      sortable: true,
    },
    {
      name: `${isForwardAuction ? "INCR" : "DECR"} (${
        eventDetails.incrementBidAmountBy === "PERCENTAGE"
          ? "%"
          : eventDetails.currency
      })`,
      width: "120px",
      selector: (data) => `${Number(data.increment)}`,
      sortable: true,
    },
    {
      name: "Start/End date & time",
      width: "190px",
      selector: (data, index) => {
        const currentTime = moment().tz(timeZone).format();
        const isLive = data.status === "live" || currentTime >= moment(data.startDateAndTime).tz(timeZone).format();

        return (
          <Typography
            className={
              isLive
                ? styles.amountSecondValueLive
                : styles.amountSecondValueUpcoming
            }
          >
            <Countdown
              key={isLive ? "live" : "upcoming"}
              className={isLive ? styles.bidLive : styles.bidUpcoming}
              date={moment(
                isLive
                  ? data.endDateAndTime
                  : data.startDateAndTime
              )
                .tz(timeZone)
                .format()}
              onComplete={() =>
                isLive ? (!data.finalizing && lotBidEnded(data, index)) : lotBidEnded()
              }
              renderer={(...args) =>
                CountdownRenderer(data.finalizing, ...args, false, isLive ? undefined : 'START')
              }
            />
          </Typography>
        );
      },
      sortable: false,
    },

    {
      name: `Lead Bid (${eventDetails.currency})`,
      width: "140px",

      selector: (data) => {
        const startDateAndTime = moment(data.startDateAndTime);
        currentTime = moment(currentTime);

        if (startDateAndTime.isAfter(currentTime)) {
          return <span>XXX</span>;
        } else if (
          currentTime.isSameOrAfter(startDateAndTime) &&
          eventDetails.showH1L1Bid === "YES"
        ) {
          return `${data.highestBid === null ? "-" : data.highestBid}`;
        } else {
          return "-";
        }
      },
      sortable: true,
    },
    {
      name: "Current Proxy Bid",
      width: "140px",
      selector: (data) => {
        return (
          <div>
            {data.proxyBidAmount >= 0 ? (
              <span style={{ fontWeight: "bold" }}>{data.proxyBidAmount}</span>
            ) : (
              <span style={{ color: "red" }}>-</span>
            )}
          </div>
        );
      },
      sortable: false,
    },
    {
      name: "Proxy Bid",
      width: "150px",
      selector: (data) => (
        <TextField
          size="small"
          type="number"
          variant="outlined"
          key={data.lotNumber}
          disabled={data.finalizing}
          InputProps={{
            inputProps: {
              min: 0,
              value: userAmount[`${data.lotNumber}`] || "",
              className: styles.auctionAmountField,
              onChange: ({ target }) => {
                handleProxyAmountChange(
                  data.lotNumber,
                  target.value,
                  userAmount
                );
              },
            },
          }}
        />
      ),
      sortable: true,
    },
    {
      name: "Proxy bid action",
      selector: (data) =>
        isOnline ? (
          data.myBidStatus.authorized === false ||
          data.auctioneerName == userData.username ||
          userData.role == "ADMIN" ? (
            <Box className={styles.auctionBidErrorBox}>Cannot Bid</Box>
          ) : data.finalizing ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "3rem",
              }}
            >
              <CircularProgress size="1.2rem" />
            </div>
          ) : (
            <Button
              disabled={userAmount[`${data.lotNumber}`] <= 0}
              onClick={() => setProxyBid(data, userAmount[`${data.lotNumber}`])}
              style={{ textTransform: "none" }}
              className={
                userAmount[`${data.lotNumber}`] <= 0
                  ? styles.auctionBidErrorBox
                  : styles.auctionBidButton
              }
            >
              {data.proxyBidAmount >= 0 ? "Update" : "Set"} proxy bid
            </Button>
          )
        ) : (
          <Box className={styles.auctionBidErrorBox}>
            Network Lost{" "}
            <WifiSVG style={{ marginLeft: "5px", fontSize: "18px" }} />
          </Box>
        ),
      sortable: false,
    },
  ];

  const createdLots =
    lots?.map((lot, index) => ({
      id: index + 1,
      location: getLocationString(lot.event, lot.location),
      ...lot,
    })) || [];

  return { columns, data: createdLots };
};

export default createProxyBidListLots;
